import { useCallback } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { ICartEntry } from '@rbi-ctg/menu';
import { IncrementorActions } from 'components/incrementor';
import analyticsProvider from 'state/analytics';
import { itemQuantityDecrementedEvent, itemQuantityIncrementedEvent } from 'state/analytics/events';
import { useOrderContext } from 'state/order';
import { routes } from 'utils/routing';

export const useCartItemEditingTools = ({ item }: { item: ICartEntry }) => {
  const { confirmRemoveFromCart, updateQuantity, editCart } = useOrderContext();
  const navigate = useNavigate();
  const location = useLocation();
  const handleEditItem = () => {
    // If its an extra take them to the extras modal
    if (item.isExtra) {
      return navigate(`${routes.cart}/extras`);
    }

    editCart(item.cartId);
    // If its a reorder item always take directly to the item. Only allow picker changes if we haven't ordered it before.
    const baseUrl = item.reorder
      ? `/menu/${item.type.toLowerCase()}-${item._id}`
      : item.url.split('?')[0];
    const editUrl = `${baseUrl}?isEdit=true`;
    navigate(editUrl, { state: { isFromCart: location.pathname === routes.cart } });
  };

  const handleRemoveItem = useCallback(() => confirmRemoveFromCart(item.cartId), [
    item,
    confirmRemoveFromCart,
  ]);

  const handleQuantityChange = useCallback(
    (quantity: number, incrementorAction: IncrementorActions) => {
      const isIncrement = incrementorAction === IncrementorActions.Increment;

      if (isIncrement) {
        analyticsProvider.logEvent(
          itemQuantityIncrementedEvent({ cartEntry: { ...item, quantity } })
        );
      } else {
        analyticsProvider.logEvent(
          itemQuantityDecrementedEvent({ cartEntry: { ...item, quantity } })
        );
      }

      updateQuantity(item.cartId, quantity);
    },
    [item, updateQuantity]
  );

  return {
    handleEditItem,
    handleRemoveItem,
    handleQuantityChange,
  };
};
